import { useState } from 'react'
import { X, ChevronLeft, ChevronRight } from 'lucide-react'

const PhotoGallery = () => {
  const [selectedImage, setSelectedImage] = useState<number | null>(null)

  // Local images from assets folder
  const photos = [
    {
      id: 1,
      src: '/assets/1.jpg',
      alt: 'Engagement photo 1',
      caption: 'Edwin on one knee'
    },
    {
      id: 2,
      src: '/assets/2.jpg',
      alt: 'Engagement photo 2',
      caption: 'Showing off'
    },
    {
      id: 3,
      src: '/assets/3.jpg',
      alt: 'Edwin and Doreen standing in front of Christmas tree',
      caption: 'Christmas 2025'
    },
    {
      id: 4,
      src: '/assets/4.jpg',
      alt: 'Our favorite liver and onions',
      caption: 'Our favorite liver and onions'
    },
    {
      id: 5,
      src: '/assets/5.jpg',
      alt: 'Gasparilla 5k',
      caption: 'Gasparilla 5k'
    },
    {
      id: 6,
      src: '/assets/6.jpg',
      alt: 'CMAs Nashville',
      caption: 'CMAs Nashville'
    },
    {
      id: 7,
      src: '/assets/7.jpg',
      alt: 'Bunny ears',
      caption: 'Bunny Ears'
    },
    {
      id: 8,
      src: '/assets/8.jpg',
      alt: 'A sunset drink',
      caption: 'A sunset drink'
    },
    {
      id: 9,
      src: '/assets/9.jpg',
      alt: 'Birthday Sunset in Clearwater',
      caption: 'Birthday Sunset in Clearwater'
    }
  ]

  const openLightbox = (index: number) => {
    setSelectedImage(index)
  }

  const closeLightbox = () => {
    setSelectedImage(null)
  }

  const nextImage = () => {
    if (selectedImage !== null) {
      setSelectedImage((selectedImage + 1) % photos.length)
    }
  }

  const prevImage = () => {
    if (selectedImage !== null) {
      setSelectedImage(selectedImage === 0 ? photos.length - 1 : selectedImage - 1)
    }
  }

  return (
    <section className="section-padding bg-cream">
      <div className="container-max">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-charcoal mb-4">
            Our Story: Beauty from Ashes
          </h2>
          <p className="text-lg text-charcoal/70 mx-auto mb-4">
            Our journey began in 2002, when we met at church and Doreen joined Edwin&apos;s team
            serving in youth ministry together for over 10 years, but it was in 2013 that our lives truly
            began to intertwine. For twelve years, we were adventurers—traveling the country,
            sharing birthdays &amp; holidays, and building a foundation of friendship that we never knew
            would become our lifeline.
            </p>
            <p className="text-lg text-charcoal/70 mx-auto mb-4">
            They say a cord of three strands is not easily broken, but for us, those strands were
            tested in the hottest of fires. When the seasons turned toward shadows, we found
            ourselves standing in the gap for one another. Through the valleys of illness, the quiet
            hallways of hospitals, and the weight of grief, we learned that God&apos;s voice isn&apos;t always a
            loud proclamation—sometimes, it&apos;s a gentle whisper saying,&quot;Trust Me.&quot;
            </p>
            <p className="text-lg text-charcoal/70 mx-auto">
            It was during the suffering that our friendship became our sanctuary. By trusting God
            through the &quot;unknowns,&quot; He revealed a beautiful new path for us both. Our marriage
            isn&apos;t just a union of two people; it&apos;s a testament to God&apos;s faithfulness, a story of hope
            born from hardship, and a love built on a foundation that nothing can shake. We are so
            grateful to share this new chapter of hope with you.
          </p>
        </div>

        {/* Photo Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {photos.map((photo, index) => (
            <div
              key={photo.id}
              className="group cursor-pointer overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
              onClick={() => openLightbox(index)}
            >
              <div className="relative aspect-square overflow-hidden">
                <img
                  src={photo.src}
                  alt={photo.alt}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300"></div>
                <div className="absolute bottom-4 left-4 right-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <p className="text-sm font-medium">{photo.caption}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Lightbox */}
        {selectedImage !== null && (
          <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
            <div className="relative max-w-4xl max-h-full">
              {/* Close button */}
              <button
                onClick={closeLightbox}
                className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>

              {/* Navigation buttons */}
              <button
                onClick={prevImage}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 w-12 h-12 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-colors"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 w-12 h-12 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-colors"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              {/* Image */}
              <img
                src={photos[selectedImage].src}
                alt={photos[selectedImage].alt}
                className="max-w-full max-h-full object-contain rounded-lg"
              />

              {/* Caption */}
              <div className="absolute bottom-4 left-4 right-4 text-center">
                <p className="text-white text-lg font-medium bg-black/50 rounded-lg px-4 py-2">
                  {photos[selectedImage].caption}
                </p>
              </div>
            </div>

            {/* Click outside to close */}
            <div
              className="absolute inset-0 -z-10"
              onClick={closeLightbox}
            ></div>
          </div>
        )}

      </div>
    </section>
  )
}

export default PhotoGallery
